// Import model
import { Proposal } from '../models/Proposal';

// Import base class serializer
import { EntityOwnedModelSerializer } from './EntityOwnedModelSerializer';

// Import dependency types (models and enums used in properties)
import { SfProposal } from '../models/SfProposal';

// Import dependency serializers (for complex types)
import { SfProposalSerializer } from './SfProposalSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Proposal - handles JSON serialization and deserialization with type validation
 */
export class ProposalSerializer {
  /**
   * Deserializes a JSON string into a Proposal instance
   */
  static deserialize(json: string): Proposal {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Proposal instance
   */
  static fromObject(obj: any): Proposal {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Proposal');
    }


    const instance: Proposal = {
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      externalId: obj['externalId'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      properties: obj['properties'] ? SfProposalSerializer.fromObject(obj['properties']) : undefined as any,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      summary: obj['summary'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Proposal instances
   */
  static deserializeArray(json: string): Proposal[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Proposal instance to a JSON string
   */
  static serialize(instance: Proposal): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Proposal instance to a plain object
   */
  static toObject(instance: Proposal): any {
    return {
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'externalId': instance.externalId,
      'isActive': instance.isActive,
      'properties': instance.properties ? SfProposalSerializer.toObject(instance.properties) : undefined,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      'summary': instance.summary,
    };
  }

  /**
   * Serializes an array of Proposal instances to a JSON string
   */
  static serializeArray(instances: Proposal[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
